import React from "react";
import { Icon } from "../icons";
import { fmtDate, todayStr } from "../lib/format";
import { computeProgressStats, pctOf } from "../lib/selectors";
import { useMcOps } from "../lib/McOpsContext";

const ProgressBar = ({ icon, color, title, num, den, sub }) => {
  const pct = pctOf(num, den);
  return (
    <div className="progress-item">
      <div className="progress-item-head">
        <div className="progress-item-title">
          <Icon name={icon} />
          {title}
        </div>
        <div className="progress-item-frac">
          {num} of {den}
        </div>
      </div>
      <div className="progress-track">
        <div className="progress-fill" style={{ width: `${pct}%`, background: color }} />
      </div>
      {sub ? <div className="progress-item-sub">{sub}</div> : null}
    </div>
  );
};

const ProgressRing = ({ pct, color }) => {
  const r = 60;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - pct / 100);
  return (
    <svg width="150" height="150" viewBox="0 0 150 150">
      <circle cx="75" cy="75" r={r} fill="none" stroke="var(--ink-100)" strokeWidth="14" />
      <circle
        cx="75"
        cy="75"
        r={r}
        fill="none"
        stroke={color}
        strokeWidth="14"
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={offset}
        transform="rotate(-90 75 75)"
      />
      <text x="75" y="70" textAnchor="middle" fontSize="30" fontWeight="800" fill="var(--ink-900)">
        {pct}%
      </text>
      <text
        x="75"
        y="92"
        textAnchor="middle"
        fontSize="10"
        fontWeight="700"
        fill="var(--ink-500)"
        style={{ textTransform: "uppercase", letterSpacing: ".03em" }}
      >
        on track
      </text>
    </svg>
  );
};

export const ProgressScreen = () => {
  const { state, progressOpen, closeProgressScreen } = useMcOps();
  const p = computeProgressStats(state);
  const s = p.s;
  const overallPct = Math.round(
    (pctOf(p.confirmNum, p.confirmDen) +
      pctOf(p.introNum, p.introDen) +
      pctOf(p.moveNum, p.moveDen) +
      pctOf(p.escNum, p.escDen)) /
      4,
  );
  const ringColor =
    overallPct >= 75
      ? "var(--success)"
      : overallPct >= 40
        ? "var(--warning)"
        : "var(--danger)";

  const touchpoints = [
    ["phone", "blue", "Outbound Calls", s.outboundCalls],
    ["phone", "purple", "Inbound Calls", s.inboundCalls],
    ["alert", "red", "Missed Calls", s.missedCalls],
    ["msg", "blue", "Texts Sent", s.textsSent],
    ["mail", "purple", "Emails Sent", s.emailsSent],
    ["note", "green", "Notes Logged", s.notesLogged],
    ["bell", "amber", "Reminders Set", s.remindersLogged],
    ["clipboard", "blue", "Confirmation PDFs Generated", s.pdfGenerated],
    ["phone", "red", "Called — Not Confirmed", s.calledNotConfirmed],
  ];

  return (
    <div className={`progress-screen${progressOpen ? " open" : ""}`}>
      <div className="progress-screen-inner">
        <div className="page-title-row">
          <div>
            <h2 className="page-title">Today's Progress</h2>
          </div>
          <button className="btn" type="button" onClick={closeProgressScreen}>
            ← Back to Landing
          </button>
        </div>

        <div className="panel" style={{ padding: "22px 24px", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 28, flexWrap: "wrap" }}>
            <ProgressRing pct={overallPct} color={ringColor} />
            <div style={{ flex: 1, minWidth: 260 }}>
              <div
                style={{
                  fontSize: "10.5px",
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: ".04em",
                  color: "var(--ink-500)",
                  marginBottom: 4,
                }}
              >
                {fmtDate(todayStr())}
              </div>
              <div
                style={{
                  fontSize: 17,
                  fontWeight: 800,
                  color: "var(--ink-900)",
                  marginBottom: 8,
                }}
              >
                Overall daily progress: {overallPct}%
              </div>
              <div style={{ fontSize: "12.5px", lineHeight: 1.6, color: "var(--ink-700)" }}>
                <Icon name="sparkle" /> {s.paragraph}
              </div>
            </div>
          </div>
        </div>

        <div className="section-label">
          <Icon name="activity" />
          Progress Toward Today's Goals
        </div>
        <div className="panel" style={{ padding: "18px 22px", marginBottom: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "22px 32px" }}>
            <ProgressBar
              icon="phone"
              color="var(--primary)"
              title="Called & Confirmed"
              num={p.confirmNum}
              den={p.confirmDen}
              sub={
                p.confirmDen - p.confirmNum > 0
                  ? `${p.confirmDen - p.confirmNum} left in the confirmation queue`
                  : "Confirmation queue clear"
              }
            />
            <ProgressBar
              icon="reply"
              color="var(--purple)"
              title="Introduction Calls Completed"
              num={p.introNum}
              den={p.introDen}
              sub={
                p.introDen - p.introNum > 0
                  ? `${p.introDen - p.introNum} clients still need an intro call`
                  : "Intro call queue clear"
              }
            />
            <ProgressBar
              icon="check"
              color="var(--success)"
              title="Moves Completed Today"
              num={p.moveNum}
              den={p.moveDen}
              sub={
                p.moveDen === 0
                  ? "No moves scheduled today"
                  : p.moveDen - p.moveNum > 0
                    ? `${p.moveDen - p.moveNum} still in progress today`
                    : "All moves scheduled today are closed out"
              }
            />
            <ProgressBar
              icon="alert"
              color="var(--warning)"
              title="Escalations Resolved"
              num={p.escNum}
              den={p.escDen}
              sub={
                p.escDen === 0
                  ? "No escalations raised today"
                  : p.escDen - p.escNum > 0
                    ? `${p.escDen - p.escNum} still open`
                    : "All resolved"
              }
            />
          </div>
        </div>

        <div className="section-label">
          <Icon name="clipboard" />
          All Touch Points Today
        </div>
        <div
          className="summary-grid"
          style={{ gridTemplateColumns: "repeat(3,1fr)", marginBottom: 16 }}
        >
          {touchpoints.map(([ic, color, label, num]) => (
            <div className="summary-stat" key={label}>
              <div className={`stat-icon ${color}`}>
                <Icon name={ic} />
              </div>
              <div>
                <div className="num">{num}</div>
                <div className="label">{label}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="section-label">
          <Icon name="alert" />
          Focus Next
        </div>
        <div className="panel" style={{ padding: "6px 14px 10px" }}>
          {p.priorityItems.length ? (
            p.priorityItems.map((it, i) => (
              <div className="list-item" key={i}>
                <div
                  className="li-icon"
                  style={{
                    background:
                      it.color === "red" ? "var(--danger-light)" : "var(--warning-light)",
                    color: it.color === "red" ? "var(--danger)" : "var(--warning)",
                  }}
                >
                  <Icon name={it.ic} />
                </div>
                <div className="li-body">
                  {it.text}
                  <div className="meta">{it.meta || ""}</div>
                </div>
              </div>
            ))
          ) : (
            <div className="panel-empty">Nothing urgent right now — nice work.</div>
          )}
        </div>
      </div>
    </div>
  );
};
